import { Controller, FormProvider } from "react-hook-form";

import camera from "assets/images/camera.svg";
import { FormField } from "components/form/FormField/FormField";
import { HealthmailLoginCardButton } from "components/HealthmailLoginCard/HealthmailLoginCardButton";
import { HealthmailLoginInfo } from "components/HealthmailLoginCard/HealthmailLoginInfo";
import { Button } from "components/ui/Button";
import { PageSection } from "components/ui/PageSection";
import { Select } from "components/ui/Select/Select";
import { useProfileForm } from "features/practice/hooks/useProfileForm";
import { onInvalidSubmit } from "utils/reportFormErrors";

import styles from "./DoctorDetailsTab.module.css";

const TITLE_OPTIONS = [
  { value: "Mr.", label: "Mr" },
  { value: "Ms", label: "Ms" },
  { value: "Mrs", label: "Mrs" },
  { value: "Miss", label: "Miss" },
  { value: "Dr", label: "Dr" },
  { value: "Master", label: "Master" },
];

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
  { value: "PreferNotToSay", label: "Prefer not to say" },
];

export const DoctorDetailsTab = () => {
  const {
    methods,
    selectedImage,
    base64Image,
    fileInputRef,
    handleCameraIconClick,
    handleFileSelection,
    onSubmit,
  } = useProfileForm();

  const { register, control, handleSubmit } = methods;

  return (
    <PageSection>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Profile details</h3>
        <HealthmailLoginCardButton variant="text" size="medium" />
      </div>

      <FormProvider {...methods}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit, onInvalidSubmit("DoctorDetailsTab"))}>
          {/* Personal details — avatar next to fields */}
          <div className={styles.personalSection}>
            <div className={styles.avatarColumn}>
              <div
                className={styles.avatarUpload}
                onClick={handleCameraIconClick}
              >
                <button type="button" className={styles.avatarButton}>
                  <img src={camera} alt="" />
                </button>
                <div className={styles.avatarCircle}>
                  {selectedImage ? (
                    <img src={URL.createObjectURL(selectedImage)} alt="" />
                  ) : (
                    <img src={base64Image} alt="" />
                  )}
                </div>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/*"
                onChange={handleFileSelection}
              />
              <HealthmailLoginInfo />
            </div>

            <div className={styles.personalFields}>
              <div className={styles.formGrid3}>
                <FormField name="title">
                  <label className={styles.formLabel}>Title</label>
                  <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onChange={field.onChange}
                        options={TITLE_OPTIONS}
                        fullWidth
                      />
                    )}
                  />
                </FormField>

                <FormField name="firstName">
                  <label className={`${styles.formLabel} requiredValidator`}>
                    First name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("firstName")}
                  />
                </FormField>

                <FormField name="lastName">
                  <label className={`${styles.formLabel} requiredValidator`}>
                    Last name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("lastName")}
                  />
                </FormField>

                <FormField name="gender">
                  <label className={styles.formLabel}>Gender</label>
                  <Controller
                    name="gender"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onChange={field.onChange}
                        options={GENDER_OPTIONS}
                        fullWidth
                        placeholder="Select"
                      />
                    )}
                  />
                </FormField>

                <FormField name="specialism">
                  <label className={styles.formLabel}>Specialism</label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("specialism")}
                  />
                </FormField>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className={styles.formGrid4}>
            <FormField name="mobile">
              <label className={styles.formLabel}>Mobile</label>
              <Controller
                name="mobile"
                control={control}
                render={({ field }) => (
                  <input
                    type="text"
                    className="form-control"
                    value={field.value || ""}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^\d\s+]/g, "");
                      if (value.includes("+")) {
                        value = "+" + value.replace(/\+/g, "");
                      }
                      field.onChange(value);
                    }}
                  />
                )}
              />
            </FormField>

            <FormField name="outOfHoursNumber">
              <label className={styles.formLabel}>Out of hours number</label>
              <Controller
                name="outOfHoursNumber"
                control={control}
                render={({ field }) => (
                  <input
                    type="text"
                    className="form-control"
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </FormField>

            <FormField name="homeNumber">
              <label className={styles.formLabel}>Home number</label>
              <input
                type="text"
                className="form-control"
                {...register("homeNumber")}
              />
            </FormField>

            <FormField name="email">
              <label className={styles.formLabel}>Email</label>
              <input
                type="email"
                className="form-control"
                disabled
                {...register("email")}
              />
            </FormField>
          </div>

          {/* Doctor numbers */}
          <div className={styles.formGrid4}>
            <FormField name="qualification">
              <label className={styles.formLabel}>Qualification</label>
              <input
                type="text"
                className="form-control"
                {...register("qualification")}
              />
            </FormField>

            <FormField name="professionalMedicalNumber">
              <label className={styles.formLabel}>
                Medical Council Number
              </label>
              <input
                type="text"
                className="form-control"
                {...register("professionalMedicalNumber")}
              />
              <p className={styles.fieldHint}>
                Doctors only. Nurses: use the NMBI number field.
              </p>
            </FormField>

            <FormField name="nmbiNumber">
              <label className={styles.formLabel}>NMBI Number</label>
              <input
                type="text"
                className="form-control"
                {...register("nmbiNumber")}
              />
              <p className={styles.fieldHint}>
                Nurses and midwives only.
              </p>
            </FormField>

            <FormField name="gmsNumber">
              <label className={styles.formLabel}>
                GMS Number / Delegated GMS
              </label>
              <input
                type="text"
                className="form-control"
                {...register("gmsNumber")}
              />
            </FormField>

            <FormField name="deaspPanelNumber">
              <label className={styles.formLabel}>DEASP Panel Number</label>
              <input
                type="text"
                className="form-control"
                {...register("deaspPanelNumber")}
              />
            </FormField>
          </div>

          <div className={styles.formActions}>
            <Button type="submit" size="large">
              Save profile settings
            </Button>
          </div>
        </form>
      </FormProvider>
    </PageSection>
  );
};
